import { Controller } from "@hotwired/stimulus"
import { MAPBOX_TOKEN } from "@appConfig/api-keys"

const WRAPPER_OPEN_CLASS = "visitor-practitioner-search-bar__wrapper--open"
const FILTERS_OPEN_CLASS = "visitor-practitioner-search-bar__filters-item--open"

export default class extends Controller {
  static targets = [
    "wrapper",
    "filters",
    "filtersItem",
    "filtersToggle",
    "geolocate",
    "geolocateSpinner",
    "latitude",
    "longitude",
    "formattedAddress",
    "input"
  ]

  static values = {
    pushAnalytics: { type: Boolean, default: false }
  }

  connect () {
    this.onOpenClick = this.onOpenClick.bind(this)
    document.addEventListener("click", this.onOpenClick)
  }

  disconnect () {
    document.removeEventListener("click", this.onOpenClick)
  }

  submitOnAddressChange (event) {
    const { latitude, longitude } = event.detail || {}

    if (!latitude || !longitude) return

    this.pushAnalyticsIfNeeded()
    this.element.requestSubmit()
  }

  toggleFilters (event) {
    event.preventDefault()
    if (!this.hasFiltersTarget) return

    this.filtersTarget.hidden = !this.filtersTarget.hidden
    this.syncFiltersState()
  }

  open (event) {
    if (event) event.preventDefault()
    if (!this.hasWrapperTarget) return

    this.wrapperTarget.classList.add(WRAPPER_OPEN_CLASS)
    if (this.hasFiltersTarget) this.filtersTarget.hidden = false
  }

  close (event) {
    if (event) event.preventDefault()
    if (!this.hasWrapperTarget) return

    this.wrapperTarget.classList.remove(WRAPPER_OPEN_CLASS)
    if (this.hasFiltersTarget) this.filtersTarget.hidden = true
    this.syncFiltersState()
  }

  syncFiltersState () {
    const open = this.hasFiltersTarget && !this.filtersTarget.hidden

    if (this.hasFiltersItemTarget) this.filtersItemTarget.classList.toggle(FILTERS_OPEN_CLASS, open)
    if (this.hasFiltersToggleTarget) this.filtersToggleTarget.setAttribute("aria-expanded", String(open))
  }

  geolocate (event) {
    event.preventDefault()
    if (!navigator.geolocation) return

    this.setGeolocating(true)
    navigator.geolocation.getCurrentPosition(
      (position) => this.reverseGeocode(position.coords),
      () => this.setGeolocating(false)
    )
  }

  onOpenClick (event) {
    if (event.target.closest("[data-open-searchbar]")) {
      this.open(event)
      return
    }

    this.closeFiltersOnOutsideClick(event)
  }

  // Desktop dropdown only: the mobile full-screen panel keeps its filters open.
  closeFiltersOnOutsideClick (event) {
    if (!this.hasFiltersTarget || this.filtersTarget.hidden) return
    if (this.hasWrapperTarget && this.wrapperTarget.classList.contains(WRAPPER_OPEN_CLASS)) return
    if (this.hasFiltersItemTarget && this.filtersItemTarget.contains(event.target)) return

    this.filtersTarget.hidden = true
    this.syncFiltersState()
  }

  reverseGeocode ({ latitude, longitude }) {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${MAPBOX_TOKEN}`

    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        const place = data.features?.[0]
        if (!place) return

        this.latitudeTarget.value = place.geometry.coordinates[1]
        this.longitudeTarget.value = place.geometry.coordinates[0]
        this.formattedAddressTarget.value = place.place_name
        if (this.hasInputTarget) this.inputTarget.value = place.place_name

        this.pushAnalyticsIfNeeded()
        this.element.requestSubmit()
      })
      .catch((error) => console.error("Error getting address from coordinates", error))
      .finally(() => this.setGeolocating(false))
  }

  setGeolocating (geolocating) {
    if (this.hasGeolocateTarget) this.geolocateTarget.hidden = geolocating
    if (this.hasGeolocateSpinnerTarget) this.geolocateSpinnerTarget.hidden = !geolocating
  }

  pushAnalyticsIfNeeded () {
    if (!this.pushAnalyticsValue) return

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ event: "practitioner_search_submit" })
  }
}
